import axios from "axios";
import { API_BASE_URL } from "./baseUrl";

/**
 * API
 * ---
 * Single axios instance for all backend calls.
 *
 * Request interceptor:
 * - Attaches the JWT from localStorage as a Bearer token.
 * - Honors an explicitly-set `Authorization` header (used by the reset/init
 *   password flow, where the token is one-shot from the URL — not localStorage).
 *
 * Response interceptor:
 * - Redirects to /login on 401, except for endpoints in NO_REDIRECT_ON_401
 *   (login itself, optional endpoints whose failure shouldn't boot the user).
 */

// Mirrors AuthenticationService.TOKEN_KEY. Kept here to avoid creating a
// circular import (api.tsx ↔ AuthenticationService.tsx).
const TOKEN_KEY = "auth-token";

const LOGIN_ENDPOINT = "/auth/distributor-user/login";

const NO_REDIRECT_ON_401 = [
  LOGIN_ENDPOINT,
  "/distributor-dashboard/partner-config",
];

const API = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

API.interceptors.request.use(
  (config) => {
    if (config.headers.Authorization) {
      return config;
    }
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl: string | undefined = error.config?.url;
    const isUnauthorized = error.response?.status === 401;
    const skipRedirect =
      !!requestUrl && NO_REDIRECT_ON_401.some((path) => requestUrl.includes(path));
    if (isUnauthorized && !skipRedirect) {
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export default API;
